import { useRef, useState, useCallback } from "react";
import { ChevronsLeftRight } from "lucide-react";

// Accessible draggable before/after comparison slider.
// Left side = Before, right side = After. `before` and `after` are React nodes
// (either <img> or styled placeholder panels).
export const BeforeAfterSlider = ({
  before,
  after,
  beforeLabel = "Before",
  afterLabel = "After",
  ariaLabel = "Before and after comparison slider",
}) => {
  const containerRef = useRef(null);
  const draggingRef = useRef(false);
  const [pos, setPos] = useState(50);

  const updateFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let p = ((clientX - rect.left) / rect.width) * 100;
    p = Math.max(0, Math.min(100, p));
    setPos(p);
  }, []);

  const onPointerDown = (e) => {
    draggingRef.current = true;
    updateFromClientX(e.clientX);
  };
  const onPointerMove = (e) => {
    if (draggingRef.current) updateFromClientX(e.clientX);
  };
  const endDrag = () => {
    draggingRef.current = false;
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") {
      setPos((p) => Math.max(0, p - 2));
      e.preventDefault();
    } else if (e.key === "ArrowRight") {
      setPos((p) => Math.min(100, p + 2));
      e.preventDefault();
    } else if (e.key === "Home") {
      setPos(0);
      e.preventDefault();
    } else if (e.key === "End") {
      setPos(100);
      e.preventDefault();
    }
  };

  return (
    <div
      ref={containerRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerLeave={endDrag}
      data-testid="ba-slider"
      className="relative aspect-[16/10] w-full touch-none select-none overflow-hidden rounded-2xl border border-white/10 bg-void"
    >
      {/* AFTER — bottom layer (fills) */}
      <div className="absolute inset-0" aria-hidden="true">
        {after}
      </div>
      {/* BEFORE — top layer, clipped from left to pos% */}
      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }} aria-hidden="true">
        {before}
      </div>

      <span className="pointer-events-none absolute left-4 top-4 rounded-full bg-void/70 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-white/80 backdrop-blur">
        {beforeLabel}
      </span>
      <span className="pointer-events-none absolute right-4 top-4 rounded-full bg-amber/90 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-void backdrop-blur">
        {afterLabel}
      </span>

      {/* divider */}
      <div className="pointer-events-none absolute inset-y-0 w-0.5 bg-amber/80" style={{ left: `${pos}%`, transform: "translateX(-50%)" }} />

      {/* handle (keyboard accessible) */}
      <button
        type="button"
        role="slider"
        aria-label={ariaLabel}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pos)}
        aria-valuetext={`${Math.round(pos)}% — ${afterLabel} revealed`}
        aria-orientation="horizontal"
        onKeyDown={onKeyDown}
        data-testid="ba-slider-handle"
        className="absolute top-1/2 z-10 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize items-center justify-center rounded-full border border-amber bg-void/90 text-amber shadow-lg outline-none transition-colors hover:bg-amber hover:text-void focus-visible:ring-2 focus-visible:ring-amber focus-visible:ring-offset-2 focus-visible:ring-offset-void"
        style={{ left: `${pos}%` }}
      >
        <ChevronsLeftRight size={18} />
      </button>
    </div>
  );
};
